import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { getInvoiceByAppointment, saveInvoiceForAppointment, sendInvoiceForAppointment } from "../api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";

const EMPTY_ITEM = { description: "", fee: 0, discount: 0, refund: 0 };
const STATUS_VARIANT = { unpaid: "destructive", partial: "warning", paid: "secondary" };
const PAYMENT_MODES = ["cash", "card", "upi", "netbanking", "other"];

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

const buildPrintHtml = (appointment, lineItems, notes, totals, clinic, tax) => {
  const addressLine = [clinic.address?.line, clinic.address?.city, clinic.address?.state, clinic.address?.pincode]
    .filter(Boolean)
    .join(", ");
  return `
  <html><head><title>Invoice</title>
  <style>
    body{font-family:sans-serif;padding:32px;color:#15181f}
    h1{font-size:20px;margin-bottom:4px}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    th,td{border-bottom:1px solid #ddd;padding:8px;text-align:left;font-size:13px}
    .totals td{font-weight:600}
    .muted{color:#6f7280;font-size:13px}
  </style></head>
  <body>
    <h1>${clinic.name} — Invoice</h1>
    ${addressLine ? `<p class="muted">${addressLine}</p>` : ""}
    ${clinic.phone || clinic.registration_no ? `<p class="muted">${[clinic.phone, clinic.registration_no && `Reg. ${clinic.registration_no}`].filter(Boolean).join(" · ")}</p>` : ""}
    <p class="muted">Patient: ${appointment.patient_id?.name} (${appointment.patient_id?.phone})</p>
    <p class="muted">Doctor: ${appointment.doctor_id?.name}</p>
    <table>
      <thead><tr><th>Description</th><th>Fee</th><th>Discount</th><th>Refund</th><th>Amount</th></tr></thead>
      <tbody>
        ${lineItems.map((i) => `<tr><td>${i.description}</td><td>${money(i.fee)}</td><td>-${money(i.discount)}</td><td>-${money(i.refund)}</td><td>${money((i.fee || 0) - (i.discount || 0) - (i.refund || 0))}</td></tr>`).join("")}
        <tr class="totals"><td>Total</td><td>${money(totals.subtotal)}</td><td>-${money(totals.discount)}</td><td>-${money(totals.refund)}</td><td>${money(totals.total)}</td></tr>
        ${tax.enabled ? `<tr><td colspan="4">${tax.label} (${tax.rate}%)</td><td>${money(tax.amount)}</td></tr>
        <tr class="totals"><td colspan="4">Grand total</td><td>${money(totals.total + tax.amount)}</td></tr>` : ""}
      </tbody>
    </table>
    ${notes ? `<p class="muted">Notes: ${notes}</p>` : ""}
  </body></html>
`;
};

const DEFAULT_CLINIC = { name: "MediPro Clinic" };

export default function GenerateInvoiceModal({ open, onOpenChange, appointment }) {
  const clinic = useSelector((s) => s.settings.data?.clinic) || DEFAULT_CLINIC;
  const billing = useSelector((s) => s.settings.data?.billing);
  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [payments, setPayments] = useState([]);
  const [newPayment, setNewPayment] = useState({ amount: "", mode: "cash", ref: "" });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !appointment) return;
    getInvoiceByAppointment(appointment._id)
      .then((res) => {
        if (res.data) {
          setInvoice(res.data);
          setLineItems(res.data.line_items);
          setNotes(res.data.notes || "");
          setPayments(res.data.payments);
        } else {
          setInvoice(null);
          setLineItems([{ description: "Consultation", fee: appointment.fee || 0, discount: 0, refund: 0 }]);
          setNotes("");
          setPayments([]);
        }
        setNewPayment({ amount: "", mode: "cash", ref: "" });
      })
      .catch((err) => toast.error(err?.message || "Could not load invoice."));
  }, [open, appointment]);

  if (!appointment) return null;

  const totals = lineItems.reduce(
    (acc, i) => ({
      subtotal: acc.subtotal + Number(i.fee || 0),
      discount: acc.discount + Number(i.discount || 0),
      refund: acc.refund + Number(i.refund || 0),
      total: acc.total + Math.max(0, Number(i.fee || 0) - Number(i.discount || 0) - Number(i.refund || 0)),
    }),
    { subtotal: 0, discount: 0, refund: 0, total: 0 }
  );
  const tax = {
    enabled: !!billing?.tax_enabled,
    label: billing?.tax_label || "Tax",
    rate: billing?.tax_rate_percent || 0,
    amount: billing?.tax_enabled ? Math.round((totals.total * (billing.tax_rate_percent || 0)) / 100) : 0,
  };
  const grandTotal = totals.total + tax.amount;
  const paidAmount = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const balance = Math.max(0, grandTotal - paidAmount);
  const status = paidAmount <= 0 ? "unpaid" : balance > 0 ? "partial" : "paid";

  const updateItem = (idx, field, value) => {
    setLineItems((items) => items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const removeItem = (idx) => setLineItems((items) => items.filter((_, i) => i !== idx));
  const addManualItem = () => setLineItems((items) => [...items, { ...EMPTY_ITEM }]);

  const addPayment = () => {
    if (!newPayment.amount || Number(newPayment.amount) <= 0) {
      toast.error("Enter a payment amount.");
      return;
    }
    setPayments((p) => [...p, { ...newPayment, amount: Number(newPayment.amount) }]);
    setNewPayment({ amount: "", mode: "cash", ref: "" });
  };
  const removePayment = (idx) => setPayments((p) => p.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (lineItems.length === 0 || lineItems.some((i) => !i.description)) {
      toast.error("Every line item needs a description.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveInvoiceForAppointment(appointment._id, { line_items: lineItems, payments, notes });
      setInvoice(res.data);
      toast.success("Invoice saved.");
    } catch (err) {
      toast.error(err?.message || "Could not save invoice.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const win = window.open("", "_blank");
    win.document.write(buildPrintHtml(appointment, lineItems, notes, totals, clinic, tax));
    win.document.close();
    win.focus();
    win.print();
  };

  const handleSend = async () => {
    if (!invoice) {
      toast.error("Save the invoice before sending it.");
      return;
    }
    setSending(true);
    try {
      await sendInvoiceForAppointment(appointment._id);
      toast.success("Invoice sent via WhatsApp.");
    } catch (err) {
      toast.error(err?.message || "Could not send invoice.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Generate Invoice {invoice && `— ${invoice.invoice_no}`}</DialogTitle>
            <Badge variant={STATUS_VARIANT[status]} className="mr-6">{status}</Badge>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 rounded-md border border-border p-3 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground">Patient</p>
            <p className="font-medium">{appointment.patient_id?.name}</p>
            <p className="text-muted-foreground">{appointment.patient_id?.phone}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground">Doctor</p>
            <p className="font-medium">{appointment.doctor_id?.name}</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Line items</Label>
            <button type="button" onClick={addManualItem} className="flex items-center gap-1 text-sm text-primary">
              <Plus className="h-3.5 w-3.5" /> Add manual item
            </button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Refund</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Input value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} />
                  </TableCell>
                  <TableCell className="w-24">
                    <Input type="number" value={item.fee} onChange={(e) => updateItem(idx, "fee", Number(e.target.value))} />
                  </TableCell>
                  <TableCell className="w-24">
                    <Input type="number" value={item.discount} onChange={(e) => updateItem(idx, "discount", Number(e.target.value))} />
                  </TableCell>
                  <TableCell className="w-24">
                    <Input type="number" value={item.refund} onChange={(e) => updateItem(idx, "refund", Number(e.target.value))} />
                  </TableCell>
                  <TableCell>{money(Math.max(0, (item.fee || 0) - (item.discount || 0) - (item.refund || 0)))}</TableCell>
                  <TableCell>
                    <button onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="font-semibold">Totals</TableCell>
                <TableCell className="font-semibold">{money(totals.subtotal)}</TableCell>
                <TableCell className="font-semibold text-destructive">-{money(totals.discount)}</TableCell>
                <TableCell className="font-semibold text-destructive">-{money(totals.refund)}</TableCell>
                <TableCell className="font-semibold text-primary">{money(totals.total)}</TableCell>
                <TableCell />
              </TableRow>
              {tax.enabled && (
                <>
                  <TableRow>
                    <TableCell colSpan={4} className="text-right text-muted-foreground">{tax.label} ({tax.rate}%)</TableCell>
                    <TableCell>{money(tax.amount)}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} className="text-right font-semibold">Grand total</TableCell>
                    <TableCell className="font-semibold text-primary">{money(grandTotal)}</TableCell>
                    <TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="space-y-1.5">
          <Label>Notes (optional)</Label>
          <textarea
            className="min-h-16 w-full rounded-md border border-input bg-background p-2 text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="flex items-center justify-between">
            <Label>Payment records</Label>
            <span className="text-xs text-muted-foreground">
              Paid {money(paidAmount)} · Balance {money(balance)}
            </span>
          </div>

          {payments.length > 0 && (
            <ul className="space-y-1 text-sm">
              {payments.map((p, idx) => (
                <li key={idx} className="flex items-center justify-between">
                  <span>
                    {money(p.amount)} · {p.mode} {p.ref && `· ${p.ref}`}
                  </span>
                  <button onClick={() => removePayment(idx)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Amount"
              value={newPayment.amount}
              onChange={(e) => setNewPayment((p) => ({ ...p, amount: e.target.value }))}
            />
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={newPayment.mode}
              onChange={(e) => setNewPayment((p) => ({ ...p, mode: e.target.value }))}
            >
              {PAYMENT_MODES.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <Input
              placeholder="Ref (optional)"
              value={newPayment.ref}
              onChange={(e) => setNewPayment((p) => ({ ...p, ref: e.target.value }))}
            />
            <Button type="button" variant="outline" onClick={addPayment}>
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleDownload}>Download</Button>
          <Button variant="outline" onClick={handleSend} disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
